import React from 'react'

import { BlockContainer } from '@Components/BlockContainer'
import { Link } from '@Components/Link'
import { Tiles } from '@Components/Tiles'
import { TilesCardLook } from '@Components/Tiles/Card/Look'
import { useTileSettings } from '@Components/Tiles/settings'
import { BlockAppearance, BlockCommon, LookTile, Maybe, PagePresentation, PageType, Scalars } from '@Generated/graphql'
import { getPins } from '@Globals/CMS/LookHelpers'
import { useCMSModalOpen } from '@Globals/CMS/Modal/Hook'
import { Image } from '@Globals/Image'
import { useTranslation } from '@Globals/Translations'

import styles from './Looks.module.scss'

export const Looks = ({
  appearance,
  autoFit = false,
  common,
  id,
  looks,
  noPadding = false,
  presentation,
  settings,
  title,
  titleOverride,
}: {
  appearance?: Maybe<BlockAppearance>
  autoFit?: boolean
  common?: Maybe<BlockCommon>
  id?: string
  looks: LookTile[]
  noPadding?: boolean
  presentation?: PagePresentation
  settings?: Maybe<Scalars['JSON']>
  title?: string
  titleOverride?: React.ReactNode
}) => {
  const tileSettings = useTileSettings(settings)
  const openModal = useCMSModalOpen()
  const { t } = useTranslation()

  if (!looks || looks.length === 0) {
    return null
  }

  return (
    <BlockContainer
      appearance={{
        ...appearance || {},
        backgroundColor: looks.length === 1 ? 'var(--neutral-100)' : appearance?.backgroundColor,
      }}
      className={styles.blockContainer}
      common={{
        ...common,
        text: looks.length === 1
          ? {
            key: 'common:discover_get_the_look',
            value: {
              text: t('common:discover_get_the_look'),
              type: 'p',
            },
          }
          : (common?.text || undefined),
        title: title || common?.title || '',
        titleElement: common?.titleElement || 'h2',
        position: looks.length === 1 ? 'left' : (common?.position || 'top'),
      }}
      commonWidth={looks.length === 1 ? 'half' : undefined}
      id={id}
      noPadding={noPadding}
      presentation={presentation || PagePresentation.Full}
      titleOverride={titleOverride}
    >
      <Tiles autoFit={autoFit} settings={tileSettings}>
        {looks.map(look => (
          <TilesCardLook
            key={look.id}
            ambassadorHandle={look.ambassadorHandle || undefined}
            cardAspectRatio={tileSettings.cardAspectRatio}
            cardSize={tileSettings.cardSize}
            cardVariant={tileSettings.cardVariant}
            image={look.asset as Image}
            onClick={tileSettings.action === 'drawer' || !look.url
              ? (e) => {
                e.preventDefault()
                e.stopPropagation()

                openModal(PageType.Look, look.id!, 'inherit', presentation || PagePresentation.Sidebar)
              }
              : undefined}
            pins={getPins(look, undefined, presentation || PagePresentation.Sidebar)}
            presentation={presentation || PagePresentation.Sidebar}
            render={tileSettings.action === 'page' && look.url
              ? props => (
                <Link href={look.url!} {...props} />
              )
              : undefined}
            renderButtonAsText={Boolean(tileSettings.action === 'page' && look.url)}
            sizes={tileSettings.width ? `${tileSettings.width}px` : undefined}
          />
        ))}
      </Tiles>
    </BlockContainer>
  )
}
